/**
 * Weather Stations Map Service
 *
 * This module provides a custom hook for managing weather station data visualization on a map.
 * It handles data fetching, state management, and map interactions.
 *
 * Key Features:
 * 1. Data Management:
 *    - Fetches weather station data
 *    - Manages station metadata
 *    - Handles data updates
 *
 * 2. Map Integration:
 *    - Integrates with map libraries
 *    - Manages station markers
 *    - Handles map interactions
 *
 * 3. State Management:
 *    - Tracks loading states
 *    - Manages error states
 *    - Handles data refresh
 *
 * 4. Performance:
 *    - Optimized data fetching
 *    - Efficient state updates
 *    - Caching mechanisms
 */

import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { stationMetaData } from "../utils/rawData";
import { useEffect } from "react";
// import { isConnected, subscribe, sendMessage } from './websocketManager';

// API configuration
const API_BASE_URL = "https://client-api.silanga.ca/aws";
const ECCC_API_BASE_URL = "https://client-api.silanga.ca/ECCC";
const NTI_API_BASE_URL = "https://www.smartatlantic.ca/erddap/tabledap"; // NTI SmartAtlantic API
const API_KEY = process.env.REACT_APP_API_TOKEN;

/**
 * Fetches data from NTI SmartAtlantic API and transforms it to match the application's data structure
 * @param {string} stationId - The station ID (UID)
 * @returns {Promise<Object>} - Transformed data for the application
 */
const fetchNTIData = async (stationId) => {
  try {
    const endpoint = `${NTI_API_BASE_URL}/${stationId}.jsonlKVP?&time%3E=max(time)-1hour&orderByDescending(%22time%22)`;

    const response = await axios.get(endpoint);

    // The response is a string with one or more JSON objects, each on a new line
    // Split the string by newlines and parse the first JSON object
    const responseData = response.data.trim().split("\n");
    const firstDataObj = JSON.parse(responseData[0]);

    /**
     * Maps NTI QC value to application QC value
     * NTI QC values:
     * -1: Missing/Invalid - Data is missing, invalid, or not evaluated
     *  0: Not Checked - Data has not been QC-checked or status is unknown
     *  1: Pass/Good - Data has passed QC checks and is considered good/valid
     *
     * App QC values:
     * 100: Good data
     *   0: Error/bad data
     */
    const mapQCValue = (qcStatus) => {
      if (qcStatus === 1) return 100; // Pass/Good -> 100 (Good)
      if (qcStatus === -1) return -10; // Missing/Invalid -> -10
      return undefined; // Not Checked (0)
    };

    // Create a data structure that matches what the application expects
    const timestamp = firstDataObj.time;

    return {
      timestamp,
      elements: {
        temperature_ext: {
          value:
            firstDataObj.air_temp_avg !== undefined
              ? firstDataObj.air_temp_avg
              : null,
          qc_value:
            firstDataObj.qc_status !== undefined
              ? mapQCValue(firstDataObj.qc_status)
              : undefined,
        },
        // Other elements not needed for map display
      },
    };
  } catch (error) {
    console.error(`Error fetching NTI data for station ${stationId}:`, error);
    return null;
  }
};

/**
 * Fetches weather data for a specific station
 * @param {Object} station - The station configuration
 * @returns {Promise<Object|null>} The weather data or null if fetch fails
 */
const fetchWeatherData = async (station) => {
  try {
    if (station.type === "NTI_Weather_Station") {
      // Fetch data from NTI API
      return await fetchNTIData(station.id);
    } else {
      //if the type defined in the stationMetaData is ECCC_Weather_Station, then use the ECCC_API_BASE_URL, otherwise use the API_BASE_URL
      const baseUrl =
        station.type === "ECCC_Weather_Station"
          ? ECCC_API_BASE_URL
          : API_BASE_URL;
      const response = await axios.get(baseUrl, {
        params: { uid: station.id },
        headers: { "x-api-key": API_KEY },
      });
      return response.data[0];
    }
  } catch (error) {
    console.error(`Error fetching data for station ${station.id}:`, error);
    return null;
  }
};

/**
 * Custom Hook: useWeatherStationsMap
 *
 * This hook provides weather station map functionality:
 * 1. Initializes map state
 * 2. Fetches station data
 * 3. Manages station markers
 * 4. Handles user interactions
 *
 * Usage:
 * const { stations, loading, error } = useWeatherStationsMap({ enabled: true });
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether the query should be enabled
 * @returns {Object} Query result with weather stations data
 */
export const useWeatherStationsMap = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();

  /**
   * Fetches and updates weather data for all stations
   * Uses optimistic updates to show loading states immediately
   * @returns {Promise<Array>} Updated station data
   */
  const fetchWeatherStations = async () => {
    // Initialize with loading state for all stations
    const initialStations = stationMetaData.map((station) => ({
      ...station,
      lat: station.default_latitude,
      lon: station.default_longitude,
      info: "Loading...",
      hasError: false,
      isLoading: true,
    }));

    // Set initial state
    queryClient.setQueryData("weatherStations", initialStations);

    // Fetch data for each station and update individually
    const promises = stationMetaData.map(async (station, index) => {
      try {
        // Skip API calls for non-operational stations
        if (station.status !== "Operational") {
          queryClient.setQueryData("weatherStations", (oldData) => {
            if (!oldData) return oldData;
            const newData = [...oldData];
            newData[index] = {
              ...station,
              lat: station.default_latitude,
              lon: station.default_longitude,
              info: "Non - Operational",
              hasError: false,
            };
            return newData;
          });
          return;
        }

        const data = await fetchWeatherData(station);

        // Update single station data as it arrives
        queryClient.setQueryData("weatherStations", (oldData) => {
          if (!oldData) return oldData;
          const newData = [...oldData];
          if (data && data.elements) {
            const temperature = data.elements.temperature_ext?.value;
            const qcValue = data.elements.temperature_ext?.qc_value;

            newData[index] = {
              ...station,
              lat: station.default_latitude,
              lon: station.default_longitude,
              info:
                qcValue !== undefined
                  ? qcValue !== 100
                    ? " - °C"
                    : `${temperature}°C`
                  : temperature !== undefined
                  ? `${temperature}°C`
                  : "N/A",
              hasError: false,
            };
          } else {
            newData[index] = {
              ...station,
              lat: station.default_latitude,
              lon: station.default_longitude,
              info: "Non - Operational",
              hasError: true,
            };
          }
          return newData;
        });
      } catch (error) {
        console.error(`Error fetching data for station ${station.id}:`, error);
      }
    });

    // Wait for all promises to complete
    await Promise.all(promises);
    return queryClient.getQueryData("weatherStations");
  };

  // Set up the query with React Query
  const query = useQuery("weatherStations", fetchWeatherStations, {
    enabled: enabled, // Only run query when enabled
    staleTime: Infinity, // Data never considered stale
    cacheTime: Infinity, // Cache never expires
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts
    refetchOnReconnect: true, // Refetch when network reconnects
  });

  /**
   * Sets up automatic hourly data refresh
   * Refreshes data at 3 minutes past each hour
   */
  useEffect(() => {
    // Only set up automatic refresh if the query is enabled
    if (!enabled) return;

    const scheduleNextRefetch = () => {
      const now = new Date();
      // Calculate time until next hour + 3 minutes
      const nextRefetchTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours() + 1, // Next hour
        3, // 3 minutes past the hour
        0
      );

      const timeUntilNextRefetch = nextRefetchTime.getTime() - now.getTime();
      return setTimeout(async () => {
        await fetchWeatherStations().then((newData) => {
          queryClient.setQueryData("weatherStations", newData);
        });
        // Schedule the next refetch
        scheduleNextRefetch();
      }, timeUntilNextRefetch);
    };

    const timeoutId = scheduleNextRefetch();

    // Cleanup on unmount
    return () => clearTimeout(timeoutId);
  }, [queryClient, enabled]);

  /**
   * Manually refreshes all weather station data
   * @returns {Promise<boolean>} True when refresh is complete
   */
  const refetchWeatherStations = async () => {
    const newData = await fetchWeatherStations();
    queryClient.setQueryData("weatherStations", newData);
    return true;
  };

  // useEffect(() => {
  //     const unsubscribe = subscribe((data) => {

  //         if (typeof data === 'object' && data.uid && data.temperature_ext !== undefined) {
  //             updateStationData(data);
  //         }
  //     });

  //     return () => {
  //         unsubscribe();
  //     };
  // }, []);

  // useEffect(() => {
  //     const keepAlive = () => {
  //         sendMessage('');
  //     };

  //     const interval = setInterval(keepAlive, 540000); // Send keep-alive every 9 minutes

  //     return () => {
  //         clearInterval(interval);
  //     };
  // }, []);

  // return { ...query, refetchWeatherStations, isConnected };
  return { ...query, refetchWeatherStations };
};

// // hooks/useWeatherStations.js
// import { useQuery, useQueryClient } from 'react-query';
// import axios from 'axios';
// import { stationMetaData } from "../utils/rawData";

// const API_BASE_URL = "https://client-api.silanga.ca/aws";
// const API_KEY = process.env.REACT_APP_API_TOKEN;

// const fetchWeatherData = async (stationId) => {
//     try {
//         const response = await axios.get(API_BASE_URL, {
//             params: { uid: stationId },
//             headers: { "x-api-key": API_KEY },
//         });
//         return response.data[0];
//     } catch (error) {
//         console.error(`Error fetching data for station ${stationId}:`, error);
//         return null;
//     }
// };

// const fetchWeatherStations = async () => {
//     const updatedStations = [];
//     for (const station of stationMetaData) {
//         const data = await fetchWeatherData(station.id);
//         if (data && data.elements) {
//             const temperature = data.elements.temperature_ext?.value;
//             updatedStations.push({
//                 ...station,
//                 lat: station.default_latitude,
//                 lon: station.default_longitude,
//                 info: `(${temperature !== undefined ? temperature + "°C" : "N/A"})`,
//                 hasError: false,
//             });
//         } else {
//             updatedStations.push({
//                 ...station,
//                 lat: station.default_latitude,
//                 lon: station.default_longitude,
//                 info: "Non - Operational",
//                 hasError: true,
//             });
//         }
//     }
//     return updatedStations;
// };

// const loadFromLocalStorage = () => {
//     const data = localStorage.getItem('weatherStations');
//     return data ? JSON.parse(data) : null;
// };

// const saveToLocalStorage = (data) => {
//     localStorage.setItem('weatherStations', JSON.stringify(data));
// };

// export const useWeatherStationsMap = () => {
//     const queryClient = useQueryClient();

//     const query = useQuery("weatherStations", async () => {
//         const cachedData = loadFromLocalStorage();
//         if (cachedData) {
//             return cachedData;
//         }
//         const freshData = await fetchWeatherStations();
//         saveToLocalStorage(freshData);
//         return freshData;
//     }, {
//         staleTime: 1 * 60 * 1000, // 1 minutes
//         cacheTime: 1 * 60 * 60 * 1000, // 1 hour
//         refetchOnWindowFocus: false,
//         refetchOnMount: false,
//         refetchOnReconnect: false,
//     });

//     const refetchWeatherStations = async () => {
//         const newData = await fetchWeatherStations();
//         const currentData = queryClient.getQueryData("weatherStations");
//         const hasNewData = JSON.stringify(newData) !== JSON.stringify(currentData);

//         if (hasNewData) {
//             queryClient.setQueryData("weatherStations", newData);
//             saveToLocalStorage(newData);
//         }

//         return hasNewData;
//     };

//     return { ...query, refetchWeatherStations };
// };
